import type { Accent, BinCard, Card, Lane, Thread } from "./types";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`API ${res.status}: ${txt}`);
  }
  return res.json();
}

export const api = {
  // Threads
  listThreads: () =>
    req<{ threads: Thread[] }>("/api/threads").then((r) => r.threads),
  createThread: (name: string, kind: "lanes" | "flat" = "lanes", accent: Accent = "lavender") =>
    req<{ thread: Thread }>("/api/threads", {
      method: "POST",
      body: JSON.stringify({ name, kind, accent }),
    }).then((r) => r.thread),
  getThread: (id: string) =>
    req<{ thread: Thread; lanes: Lane[]; cards: Card[] }>(`/api/threads/${id}`),
  updateThread: (
    id: string,
    patch: Partial<Pick<Thread, "name" | "accent" | "kind" | "sort_order">> & {
      archived?: 0 | 1 | boolean;
      pinned?: 0 | 1 | boolean;
    }
  ) =>
    req<{ thread: Thread }>(`/api/threads/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }).then((r) => r.thread),
  deleteThread: (id: string) =>
    req<{ ok: true }>(`/api/threads/${id}`, { method: "DELETE" }),

  // Lanes
  createLane: (thread_id: string, name = "New lane", accent: Accent = "sage") =>
    req<{ lane: Lane }>("/api/lanes", {
      method: "POST",
      body: JSON.stringify({ thread_id, name, accent }),
    }).then((r) => r.lane),
  updateLane: (id: string, patch: Partial<Pick<Lane, "name" | "accent" | "sort_order">>) =>
    req<{ lane: Lane }>(`/api/lanes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }).then((r) => r.lane),
  deleteLane: (id: string) =>
    req<{ ok: true }>(`/api/lanes/${id}`, { method: "DELETE" }),

  // Cards
  createCard: (thread_id: string, lane_id: string | null, title: string) =>
    req<{ card: Card }>("/api/cards", {
      method: "POST",
      body: JSON.stringify({ thread_id, lane_id, title }),
    }).then((r) => r.card),
  updateCard: (id: string, patch: Partial<Pick<Card, "title" | "body" | "lane_id" | "accent" | "sort_order">>) =>
    req<{ card: Card }>(`/api/cards/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }).then((r) => r.card),
  deleteCard: (id: string) =>
    req<{ ok: true }>(`/api/cards/${id}`, { method: "DELETE" }),

  // Reorder (bulk)
  reorder: (kind: "card" | "lane" | "thread", items: Array<{ id: string; sort_order: number; lane_id?: string | null }>) =>
    req<{ ok: true }>("/api/reorder", {
      method: "POST",
      body: JSON.stringify({ kind, items }),
    }),

  // Bin
  listBin: () => req<{ cards: BinCard[] }>("/api/bin").then((r) => r.cards),
  restoreCard: (id: string) =>
    req<{ card: Card }>(`/api/cards/${id}/restore`, { method: "POST" }).then(
      (r) => r.card
    ),
  purgeCard: (id: string) =>
    req<{ ok: true }>(`/api/cards/${id}/purge`, { method: "POST" }),
};
